import { File, FileText, FileImage, FileVideo, FileAudio, FileCode, Folder } from 'lucide-react'
import { Presentation as PresentationIcon } from 'lucide-react'
import { FileSpreadsheet as FileSpreadsheetIcon } from 'lucide-react'

// Re-export with original names for compatibility
const FileSpreadsheet = FileSpreadsheetIcon
const Presentation = PresentationIcon
import { FileType } from '@/types/enums'
import { cn } from '@/lib/utils/cn'

interface NodeFileTypeIconProps {
  type: FileType | string
  className?: string
}

const iconMap: Record<FileType, React.ComponentType<{ className?: string }>> = {
  [FileType.File]: File,
  [FileType.Folder]: Folder,
  [FileType.Image]: FileImage,
  [FileType.Video]: FileVideo,
  [FileType.Audio]: FileAudio,
  [FileType.PDF]: FileText,
  [FileType.Document]: FileText,
  [FileType.Spreadsheet]: FileSpreadsheet,
  [FileType.Presentation]: Presentation,
  [FileType.Code]: FileCode,
  [FileType.Other]: File,
}

const colorMap: Record<FileType, string> = {
  [FileType.File]: 'text-gray-500',
  [FileType.Folder]: 'text-blue-500',
  [FileType.Image]: 'text-purple-500',
  [FileType.Video]: 'text-red-500',
  [FileType.Audio]: 'text-orange-500',
  [FileType.PDF]: 'text-red-600',
  [FileType.Document]: 'text-blue-600',
  [FileType.Spreadsheet]: 'text-green-600',
  [FileType.Presentation]: 'text-orange-600',
  [FileType.Code]: 'text-slate-600',
  [FileType.Other]: 'text-gray-400',
}

export function NodeFileTypeIcon({ type, className }: NodeFileTypeIconProps) {
  // Check if the type is a valid FileType enum value
  const isValidFileType = Object.values(FileType).includes(type as FileType)

  // Use the correct icon based on validity, fallback to FileType.Other for invalid types
  const fileType = isValidFileType ? (type as FileType) : FileType.Other
  const Icon = iconMap[fileType]
  const colorClass = colorMap[fileType]

  // If type is invalid, silently use fallback (no console error to avoid noise)
  // This handles cases where documents might have incorrect type values like 'Task' instead of a FileType

  return <Icon className={cn(colorClass, className)} />
}

interface NodeFileTypeBadgeProps {
  type: FileType
  className?: string
}

export function NodeFileTypeBadge({ type, className }: NodeFileTypeBadgeProps) {
  const Icon = iconMap[type]
  const colorClass = colorMap[type]

  return (
    <div
      className={cn(
        'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium',
        'bg-secondary/50',
        className
      )}
    >
      <Icon className={cn(colorClass, 'h-3.5 w-3.5')} />
      <span>{type}</span>
    </div>
  )
}
